import React, { useState } from 'react';
import { Card, Table, Button, Select, Typography, Upload, message, Row, Col, Tag, Space } from 'antd';
import { DatabaseOutlined, UploadOutlined, SearchOutlined, FolderOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

interface HistoricalDataItem {
    id: string;
    station: string;
    stationType: string;
    dataType: string;
    date: string;
    timeRange: string;
    dataPoints: number;
    avgPower: number;
    maxPower: number;
    minPower: number;
    fileSize: string;
    storageDir: string;
    fileName: string;
    status: string;
}

const HistoricalDataManagement: React.FC = () => {
    const [selectedStation, setSelectedStation] = useState<string>('all');
    const [selectedDataType, setSelectedDataType] = useState<string>('all');
    const [uploadStation, setUploadStation] = useState<string>('');
    const [uploadDataType, setUploadDataType] = useState<string>('');

    // 场站列表
    const stations = [
        { id: '1', name: '风电场A-1号机组', type: '风力发电', storageDir: '/data/wind/station_a_01/' },
        { id: '2', name: '风电场A-2号机组', type: '风力发电', storageDir: '/data/wind/station_a_02/' },
        { id: '3', name: '光伏场B-1号方阵', type: '光伏发电', storageDir: '/data/solar/station_b_01/' },
        { id: '4', name: '水电站C-1号机组', type: '水力发电', storageDir: '/data/hydro/station_c_01/' },
    ];

    // 数据类型选项
    const dataTypeOptions = [
        { value: 'historical', label: '历史数据', color: 'blue' },
        { value: 'd1', label: 'D-1数据', color: 'green' }
    ];

    // 生成90天历史数据的函数
    const generateHistoricalData = (): HistoricalDataItem[] => {
        const data: HistoricalDataItem[] = [];
        const stations = [
            { name: '风电场A-1号机组', type: '风力发电', storageBase: '/data/wind/station_a_01/' },
            { name: '风电场A-2号机组', type: '风力发电', storageBase: '/data/wind/station_a_02/' },
            { name: '光伏场B-1号方阵', type: '光伏发电', storageBase: '/data/solar/station_b_01/' },
            { name: '水电站C-1号机组', type: '水力发电', storageBase: '/data/hydro/station_c_01/' },
        ];

        let id = 1;

        // 为每个场站生成一个历史数据文件（包含90天数据）
        stations.forEach((station) => {
            // 历史数据 - 每个场站一个文件包含90天数据
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - 90);

            data.push({
                id: String(id++),
                station: station.name,
                stationType: station.type,
                dataType: 'historical',
                date: `${startDate.toISOString().split('T')[0]} ~ ${endDate.toISOString().split('T')[0]}`,
                timeRange: station.type === '光伏发电' ? '06:00-18:00 (90天)' : '00:00-23:59 (90天)',
                dataPoints: station.type === '光伏发电' ? 144 * 90 : 288 * 90, // 90天的总数据点
                avgPower: station.type === '水力发电' ?
                    47.5 :
                    station.type === '光伏发电' ?
                        4.1 :
                        3.2,
                maxPower: station.type === '水力发电' ? 50 : station.type === '光伏发电' ? 5.2 : 3.5,
                minPower: station.type === '光伏发电' ? 0 : station.type === '水力发电' ? 35 : 0.3,
                fileSize: `${(180 + Math.random() * 50).toFixed(1)}MB`, // 90天数据文件较大
                storageDir: `${station.storageBase}historical/`,
                fileName: 'historical_90days_data.csv',
                status: '完整'
            });
        });

        // 生成过去30天的D-1预测数据（每隔2-3天一个文件）
        for (let i = 30; i >= 1; i--) {
            if (i % 3 === 0) { // 每3天生成一次D-1数据
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                const dateFormatted = dateStr.replace(/-/g, '');

                stations.forEach((station) => {
                    data.push({
                        id: String(id++),
                        station: station.name,
                        stationType: station.type,
                        dataType: 'd1',
                        date: dateStr,
                        timeRange: station.type === '光伏发电' ? '06:00-18:00' : '00:00-23:59',
                        dataPoints: station.type === '光伏发电' ? 144 : 288,
                        avgPower: station.type === '水力发电' ?
                            46 + Math.random() * 8 :
                            station.type === '光伏发电' ?
                                3.2 + Math.random() * 2.5 :
                                2.6 + Math.random() * 1.2,
                        maxPower: station.type === '水力发电' ? 50 : station.type === '光伏发电' ? 5.2 : 3.5,
                        minPower: station.type === '光伏发电' ? 0 : station.type === '水力发电' ? 38 : 0.5,
                        fileSize: `${(1.9 + Math.random() * 1.3).toFixed(1)}MB`,
                        storageDir: `${station.storageBase}d1/`,
                        fileName: `${dateFormatted}_d1_forecast.csv`,
                        status: '完整'
                    });
                });
            }
        }

        return data;
    };

    // 历史功率数据（90天数据）
    const [historicalData, setHistoricalData] = useState(() => generateHistoricalData());

    const columns = [
        {
            title: '场站名称',
            dataIndex: 'station',
            key: 'station',
            render: (text: string, record: any) => (
                <div>
                    <div>{text}</div>
                    <Tag color="blue">{record.stationType}</Tag>
                </div>
            ),
        },
        {
            title: '数据类型',
            dataIndex: 'dataType',
            key: 'dataType',
            render: (dataType: string) => {
                const typeConfig = dataTypeOptions.find(opt => opt.value === dataType);
                return <Tag color={typeConfig?.color || 'default'}>{typeConfig?.label || dataType}</Tag>;
            },
        },
        {
            title: '数据点数',
            dataIndex: 'dataPoints',
            key: 'dataPoints',
        },
        {
            title: '存储目录',
            key: 'storage',
            render: (_: any, record: any) => (
                <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        <FolderOutlined style={{ marginRight: '4px' }} />
                        {record.storageDir}
                    </div>
                    <div style={{ fontSize: '12px' }}>{record.fileName}</div>
                </div>
            ),
        },
        {
            title: '文件大小',
            dataIndex: 'fileSize',
            key: 'fileSize',
        },
        {
            title: '操作',
            key: 'action',
            render: () => (
                <Space size="small">
                    <Button type="link" size="small">下载</Button>
                    <Button type="link" size="small">查看</Button>
                    <Button type="link" size="small">分析</Button>
                    <Button type="link" size="small" danger>删除</Button>
                </Space>
            ),
        },
    ];

    // 查询功能
    const handleSearch = () => {
        // 这里实现查询逻辑
        let filteredData = [...historicalData];

        // 按场站筛选
        if (selectedStation && selectedStation !== 'all') {
            filteredData = filteredData.filter(item => item.station === selectedStation);
        }

        // 按数据类型筛选
        if (selectedDataType && selectedDataType !== 'all') {
            filteredData = filteredData.filter(item => item.dataType === selectedDataType);
        }

        message.success(`查询到 ${filteredData.length} 条记录`);
        // 这里应该更新显示的数据，为了演示目的暂时只显示消息
    };

    const uploadProps = {
        name: 'file',
        action: '/upload',
        beforeUpload: (file: any) => {
            if (!uploadStation) {
                message.error('请先选择要上传到的场站');
                return false;
            }
            if (!uploadDataType) {
                message.error('请先选择数据类型');
                return false;
            }
            return true;
        },
        onChange(info: any) {
            if (info.file.status === 'done') {
                const dataTypeLabel = dataTypeOptions.find(opt => opt.value === uploadDataType)?.label || uploadDataType;
                message.success(`${info.file.name} 已成功上传到 ${uploadStation} (${dataTypeLabel})`);
            } else if (info.file.status === 'error') {
                message.error(`${info.file.name} 上传失败`);
            }
        },
    };

    return (
        <div className="page-transition">
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <DatabaseOutlined style={{ marginRight: '8px' }} />
                        历史功率数据管理
                    </Title>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <Select
                            value={uploadStation}
                            onChange={setUploadStation}
                            placeholder="选择场站"
                            style={{ width: 180 }}
                        >
                            {stations.map(station => (
                                <Option key={station.id} value={station.name}>
                                    {station.name}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={uploadDataType}
                            onChange={setUploadDataType}
                            placeholder="选择数据类型"
                            style={{ width: 120 }}
                        >
                            {dataTypeOptions.map(type => (
                                <Option key={type.value} value={type.value}>
                                    {type.label}
                                </Option>
                            ))}
                        </Select>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} type="primary">
                                上传数据
                            </Button>
                        </Upload>
                    </div>
                </div>

                {/* 查询条件 */}
                <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
                    <Row gutter={16} align="middle">
                        <Col span={6}>
                            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>场站选择:</div>
                            <Select
                                value={selectedStation}
                                onChange={setSelectedStation}
                                style={{ width: '100%' }}
                                placeholder="选择场站"
                            >
                                <Option value="all">全部场站</Option>
                                {stations.map(station => (
                                    <Option key={station.id} value={station.name}>
                                        {station.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>数据类型:</div>
                            <Select
                                value={selectedDataType}
                                onChange={setSelectedDataType}
                                style={{ width: '100%' }}
                                placeholder="选择数据类型"
                            >
                                <Option value="all">全部类型</Option>
                                {dataTypeOptions.map(type => (
                                    <Option key={type.value} value={type.value}>
                                        {type.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                style={{ marginTop: '24px' }}
                            >
                                查询
                            </Button>
                        </Col>
                        <Col span={8}>
                            <div style={{ marginTop: '24px', fontSize: '12px', color: '#666' }}>
                                <div>已显示 {historicalData.length} 条记录 (90天数据)</div>
                                <div>总存储路径: {stations.length} 个目录</div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Table
                    columns={columns}
                    dataSource={historicalData}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                    }}
                    scroll={{ x: 1400 }}
                />
            </Card>
        </div>
    );
};

export default HistoricalDataManagement; 